import { BiteServiceName, BiteServicePorts, Environment, EnvironmentHelper } from '@biteinc/enums';

import { getHttpServiceUrlConfig } from './bite_service_url.js';

export function biteApiUrl(env: Environment): string {
  if (EnvironmentHelper.isPreview()) {
    const pr = EnvironmentHelper.porterPrNumber();
    return `https://pr-${pr}-maitred.getbite.net`;
  }
  switch (env) {
    case Environment.PROD:
      return 'https://api.getbite.com';
    case Environment.STAGING:
      return 'https://api-staging.getbite.com';
    case Environment.SANDBOX:
      return 'https://api-sandbox.getbite.com';
    case Environment.DEV: {
      const ngrokHost = getEnvVar('NGROK_MAITRED_HOST');
      if (ngrokHost) {
        return `https://${ngrokHost}`;
      }
      const override = getEnvVar('MAITRED_URL');
      if (override) {
        return override;
      }
      return `http://${getIpAddress()}:${BiteServicePorts.Maitred}`;
    }
    case Environment.TEST: {
      const override = getEnvVar('MAITRED_URL');
      if (override) {
        return override;
      }
      return `http://${getIpAddress()}:${BiteServicePorts.Maitred}`;
    }
  }
}

export function recommendationServiceUrl(env: Environment): string {
  switch (env) {
    case Environment.PROD:
    case Environment.STAGING:
    case Environment.SANDBOX:
      // In hosted environments we route calls to /v2/recommendations at the porter level
      return biteApiUrl(env);
    case Environment.TEST:
    case Environment.DEV: {
      const override = getEnvVar('RECOMMENDATION_APP_URL');
      if (override) {
        return override;
      }
      return `http://${getIpAddress()}:${BiteServicePorts.RecommendationApp}`;
    }
  }
}

/**
 * Public base URL of the standalone MCP app (`packages/mcp-app`). This is the
 * OAuth *resource* identity clients see, so it must be the host that actually
 * serves `/mcp` — maitred still serves its own `/mcp` during the dual run.
 */
export function mcpBaseUrl(env: Environment): string {
  if (EnvironmentHelper.isPreview()) {
    const pr = EnvironmentHelper.porterPrNumber();
    return `https://pr-${pr}-mcp-app.getbite.net`;
  }
  switch (env) {
    case Environment.PROD:
      return 'https://mcp.getbite.com';
    case Environment.STAGING:
      return 'https://mcp-staging.getbite.com';
    case Environment.SANDBOX:
      return 'https://mcp-sandbox.getbite.com';
    case Environment.DEV:
    case Environment.TEST: {
      const override = getEnvVar('MCP_APP_URL');
      if (override) {
        return override;
      }
      return `http://${getIpAddress()}:${BiteServicePorts.McpApp}`;
    }
  }
}

export function biteReceiptUrl(env: Environment): string {
  switch (env) {
    case Environment.PROD:
      return 'https://txt.bitekiosk.io';
    case Environment.STAGING:
    case Environment.SANDBOX:
    case Environment.TEST:
    case Environment.DEV:
      return bureauUrl(env);
  }
}

export function serviceUrl(env: Environment, serviceName: BiteServiceName): string {
  if (EnvironmentHelper.isPreview()) {
    const pr = EnvironmentHelper.porterPrNumber();
    switch (serviceName) {
      case BiteServiceName.RecommendationApp:
        return `https://pr-${pr}-recommendation.getbite.net`;
      default:
        throw new Error(`Service ${serviceName} is not available in preview`);
    }
  }
  switch (env) {
    case Environment.PROD: {
      switch (serviceName) {
        case BiteServiceName.RecommendationApp:
          return 'http://recommendation.getbite.com';
        case BiteServiceName.Bureau:
        case BiteServiceName.BuildAndDeploy:
        case BiteServiceName.Clock:
        case BiteServiceName.DbCollectionWatcher:
        case BiteServiceName.Eclair:
        case BiteServiceName.Flash:
        case BiteServiceName.Garcon:
        case BiteServiceName.Maitred:
        case BiteServiceName.McpApp:
        case BiteServiceName.JobWorker:
        case BiteServiceName.Scripts:
        case BiteServiceName.QueueConsumerInternal:
        case BiteServiceName.QueueConsumerOperational:
        case BiteServiceName.QueueConsumerOrders:
        case BiteServiceName.QueueConsumerQuicksand:
        case BiteServiceName.QueueConsumerQuicksandSmall:
        case BiteServiceName.QueueConsumerWebhooks:
        case BiteServiceName.Vitrine:
        case BiteServiceName.WebhookReceiver:
          throw new Error(`Service ${serviceName} is not a public service`);
        default: {
          serviceName satisfies never;
          throw new Error(`Unknown service name ${serviceName}`);
        }
      }
    }
    case Environment.STAGING: {
      switch (serviceName) {
        case BiteServiceName.RecommendationApp:
          return 'http://recommendation-staging.getbite.com';
        case BiteServiceName.Bureau:
        case BiteServiceName.BuildAndDeploy:
        case BiteServiceName.Clock:
        case BiteServiceName.DbCollectionWatcher:
        case BiteServiceName.Eclair:
        case BiteServiceName.Flash:
        case BiteServiceName.Garcon:
        case BiteServiceName.Maitred:
        case BiteServiceName.McpApp:
        case BiteServiceName.JobWorker:
        case BiteServiceName.Scripts:
        case BiteServiceName.QueueConsumerInternal:
        case BiteServiceName.QueueConsumerOperational:
        case BiteServiceName.QueueConsumerOrders:
        case BiteServiceName.QueueConsumerQuicksand:
        case BiteServiceName.QueueConsumerQuicksandSmall:
        case BiteServiceName.QueueConsumerWebhooks:
        case BiteServiceName.Vitrine:
        case BiteServiceName.WebhookReceiver:
          throw new Error(`Service ${serviceName} is not a public service`);
        default: {
          serviceName satisfies never;
          throw new Error(`Unknown service name ${serviceName}`);
        }
      }
    }
    case Environment.SANDBOX: {
      switch (serviceName) {
        case BiteServiceName.RecommendationApp:
          return 'http://recommendation-sandbox.getbite.com';
        case BiteServiceName.Bureau:
        case BiteServiceName.BuildAndDeploy:
        case BiteServiceName.Clock:
        case BiteServiceName.DbCollectionWatcher:
        case BiteServiceName.Eclair:
        case BiteServiceName.Flash:
        case BiteServiceName.Garcon:
        case BiteServiceName.Maitred:
        case BiteServiceName.McpApp:
        case BiteServiceName.JobWorker:
        case BiteServiceName.Scripts:
        case BiteServiceName.QueueConsumerInternal:
        case BiteServiceName.QueueConsumerOperational:
        case BiteServiceName.QueueConsumerOrders:
        case BiteServiceName.QueueConsumerQuicksand:
        case BiteServiceName.QueueConsumerQuicksandSmall:
        case BiteServiceName.QueueConsumerWebhooks:
        case BiteServiceName.Vitrine:
        case BiteServiceName.WebhookReceiver:
          throw new Error(`Service ${serviceName} is not a public service`);
        default: {
          serviceName satisfies never;
          throw new Error(`Unknown service name ${serviceName}`);
        }
      }
    }
    case Environment.DEV:
    case Environment.TEST:
      return `127.0.0.1`;
  }
}

export function internalServiceUrl(
  env: Environment,
  serviceName: BiteServiceName,
): string | undefined {
  const serviceConfig = getHttpServiceUrlConfig(serviceName);
  if (serviceConfig === undefined) {
    return undefined;
  }

  const internalOverride = getEnvVar(serviceConfig.internalUrlEnvVar);
  if (internalOverride) {
    return internalOverride;
  }

  if (EnvironmentHelper.isDevOrTest(env)) {
    if (getEnvVar('BITE_PROXY') !== '1') {
      const localOverride = getEnvVar(serviceConfig.urlEnvVar);
      if (localOverride) {
        return localOverride;
      }
    }
    return `http://${getIpAddress()}:${serviceConfig.port}`;
  }

  const porterAppServiceName = getEnvVar('PORTER_APP_SERVICE_NAME');
  if (porterAppServiceName && !EnvironmentHelper.isPreview()) {
    switch (env) {
      case Environment.PROD:
      case Environment.STAGING:
      case Environment.SANDBOX: {
        const porterApplication = serviceConfig.porterApplicationByEnv[env];
        if (!porterApplication) {
          return undefined;
        }
        return `http://${porterApplication}-web.default.svc.cluster.local.:${serviceConfig.port}`;
      }
      case Environment.DEV:
      case Environment.TEST:
        return undefined;
    }
  }

  return undefined;
}

/**
 * @description This is a good centralized method to rewrite our asset urls if there's a need
 */
export function biteAssetUrlWithNewHost<T extends string | undefined>(
  originalAssetUrl: T,
  newHost: string | undefined,
): T {
  if (newHost && originalAssetUrl) {
    const host = originalAssetUrl.substring(
      0,
      // We can't use the node url library here since this code gets imported on the front end.
      // Find the host by taking all characters through the first slash.
      originalAssetUrl.indexOf('/', originalAssetUrl.indexOf('://') + 3) + 1,
    );
    const newHostWithSlash = newHost.endsWith('/') ? newHost : `${newHost}/`;
    return originalAssetUrl.replace(host, newHostWithSlash) as T;
  }
  return originalAssetUrl;
}

export function bureauUrl(env: Environment): string {
  if (EnvironmentHelper.isPreview()) {
    const pr = EnvironmentHelper.porterPrNumber();
    return `https://pr-${pr}-bureau.getbite.net`;
  }
  switch (env) {
    case Environment.PROD:
      return 'https://admin.getbite.com';
    case Environment.SANDBOX:
      return 'https://admin-sandbox.getbite.com';
    case Environment.STAGING:
      return 'https://admin-staging.getbite.com';
    case Environment.TEST:
    case Environment.DEV: {
      const override = getEnvVar('BUREAU_URL');
      if (override) {
        return override;
      }
      return `http://${getEnvVar('BUREAU_LOCALHOST') ?? getIpAddress()}:${BiteServicePorts.Bureau}`;
    }
  }
}

export function webhookReceiverBaseUrl(env: Environment): string {
  if (EnvironmentHelper.isPreview()) {
    const pr = EnvironmentHelper.porterPrNumber();
    return `https://pr-${pr}-webhook-receiver.getbite.net`;
  }
  switch (env) {
    case Environment.PROD:
      return 'https://webhook.getbite.com';
    case Environment.STAGING:
      return 'https://webhook-staging.getbite.com';
    case Environment.SANDBOX:
      return 'https://webhook-sandbox.getbite.com';
    case Environment.DEV:
    case Environment.TEST: {
      const override = getEnvVar('WEBHOOK_RECEIVER_URL');
      if (override) {
        return override;
      }
      return `http://${getIpAddress()}:${BiteServicePorts.WebhookReceiver}`;
    }
  }
}

/** Read env var safely — works in both Node and browser contexts */
function getEnvVar(name: string): string | undefined {
  return (globalThis as any)?.process?.env?.[name] as string | undefined;
}

function getIpAddress(): string {
  const ip = (globalThis as any)?.process?.env?.LOCAL_IP;
  return ip ?? 'localhost';
}
